import type { FileUploadData } from "@qw/ui";
import { createContext, type RefObject, useContext } from "react";
import type { DropzoneState } from "react-dropzone";
import type { UseFormReturn } from "react-hook-form";

import type { ChatFormValues } from "./chat-form.types";

export interface ChatFormContextValue {
  // React Hook Form methods
  formMethods: UseFormReturn<ChatFormValues>;

  // Text input
  input: string;
  setInput: (input: string) => void;

  // Files and dropzone
  files: FileUploadData[];
  setFiles: (files: FileUploadData[]) => void;
  addFile: (file: FileUploadData) => void;
  removeFile: (index: number) => void;
  clearFiles: () => void;
  isUploadingFiles: boolean;
  dropzone: DropzoneState;

  // Transcription
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | undefined>;
  cancelRecording: () => void;
  isIdle: boolean;
  isRecording: boolean;
  isProcessing: boolean;
  isError: boolean;
  audioLevels: number[];

  // Form submission
  submitForm: () => void;

  // State helpers
  isStreaming: boolean;
  hasInput: boolean;
  hasFiles: boolean;
  canEdit: boolean;
  canSubmit: boolean;

  // Refs
  textInputRef: RefObject<HTMLTextAreaElement | null>;
  submitButtonRef: RefObject<HTMLButtonElement | null>;
}

export const ChatFormContext = createContext<ChatFormContextValue | null>(null);

export const useChatForm = (): ChatFormContextValue => {
  const context = useContext(ChatFormContext);

  if (!context) {
    throw new Error("useChatForm must be used within a ChatFormProvider");
  }

  return context;
};
